<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Advertiser Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('TABLES.AdRequests.advertiser_name')"
                v-model.trim="filterOptions.advertiser_name"
              />
              <!-- End:: Advertiser Name Input -->

              <!-- Start:: Ad Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('TABLES.AdRequests.ad_name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Ad Name Input -->

              <!-- Start:: Shop Type Select -->
              <base-select-input
                col="4"
                :optionsList="shopTypes"
                :placeholder="$t('TABLES.AdRequests.shop_type')"
                v-model="filterOptions.shop_type"
              />
              <!-- End:: Shop Type Select -->

              <!-- Start:: Status Select -->
              <!-- <base-select-input
                col="3"
                :optionsList="statusOptions"
                :placeholder="$t('TABLES.AdRequests.status')"
                v-model="filterOptions.status"
              /> -->
              <!-- End:: Status Select -->

              <!-- Start:: Date From Input -->
              <base-picker-input
                col="6"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_from')"
                v-model="filterOptions.from"
              />
              <!-- End:: Date From Input -->

              <!-- Start:: Date To Input -->
              <base-picker-input
                col="6"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_to')"
                v-model="filterOptions.to"
              />
              <!-- End:: Date To Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TABLES.AdRequests.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Advertiser Name -->
        <template v-slot:[`item.advertiser_name`]="{ item }">
          <span class="text-danger" v-if="!item.user || !item.user.user">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.user.user.name }} </span>
        </template>
        <!-- End:: Advertiser Name -->

        <!-- Start:: Ad Name -->
        <template v-slot:[`item.name`]="{ item }">
          <span class="text-danger" v-if="!item.name">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.name }} </span>
        </template>
        <!-- End:: Ad Name -->

        <!-- Start:: Shop Type -->
        <template v-slot:[`item.shop_type`]="{ item }">
          <span> {{ item._shop_type || "-" }} </span>
        </template>
        <!-- End:: Shop Type -->

        <!-- Start:: Location -->
        <template v-slot:[`item.location`]="{ item }">
          <span v-if="item.region && item.city">
            {{ item.region.name }} - {{ item.city.name }}
          </span>
          <span v-else class="text-danger">
            {{ $t("TABLES.noData") }}
          </span>
        </template>
        <!-- End:: Location -->

        <!-- Start:: Rent Price -->
        <template v-slot:[`item.rent_price`]="{ item }">
          <span> {{ item.rent_price || "-" }} </span>
        </template>
        <!-- End:: Rent Price -->

        <!-- Start:: Request Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <span> {{ item.created_at || "-" }} </span>
        </template>
        <!-- End:: Request Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            :class="{
              'text-warning': item.status === 'pending',
              'text-success': item.status === 'published',
              'text-danger': item.status === 'rejected',
            }"
          >
            {{ item._ad_status || "-" }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Show Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item.id)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <!-- Accept Button -->
            <a-tooltip placement="bottom" v-if="item.status === 'pending'">
              <template slot="title">
                <span>{{ $t("BUTTONS.accept") }}</span>
              </template>
              <button class="btn_success" @click="acceptRequest(item.id)">
                <i class="fal fa-check"></i>
              </button>
            </a-tooltip>

            <!-- Reject Button -->
            <a-tooltip placement="bottom" v-if="item.status === 'pending'">
              <template slot="title">
                <span>{{ $t("BUTTONS.reject") }}</span>
              </template>
              <button class="btn_delete" @click="openRejectModal(item.id)">
                <i class="fal fa-times"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <div class="pagination_container">
        <v-pagination
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          color="#404b3e"
        ></v-pagination>
      </div>
      <!--  =========== End:: Pagination =========== -->
    </main>

    <!-- Start:: Reject Reason Modal -->
    <v-dialog v-model="rejectModalIsOpen" max-width="500">
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.reject_reason") }}
        </v-card-title>
        <v-card-text>
          <base-input
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.reject_reason')"
            v-model.trim="rejectReason"
            required
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            @click="confirmReject"
            :disabled="!rejectReason || isWaitingRequest"
          >
            {{ $t("BUTTONS.confirm") }}
          </v-btn>
          <v-btn
            class="modal_cancel_btn"
            @click="closeRejectModal"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Reject Reason Modal -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "AllAdRequests",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        advertiser_name: null,
        name: null,
        shop_type: null,
        status: null,
        from: null,
        to: null,
      },
      shopTypes: [
        {
          id: "shack",
          name: this.$t("TABLES.AdRequests.shack"),
        },
        {
          id: "exposition",
          name: this.$t("TABLES.AdRequests.exposition"),
        },
      ],
      statusOptions: [
        {
          id: "pending",
          name: this.$t("TABLES.AdRequests.pending"),
        },
        {
          id: "published",
          name: this.$t("TABLES.AdRequests.published"),
        },
        {
          id: "rejected",
          name: this.$t("TABLES.AdRequests.rejected"),
        },
      ],
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.AdRequests.serial"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.advertiser_name"),
          value: "advertiser_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.ad_name"),
          value: "name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.shop_type"),
          value: "shop_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.location"),
          value: "location",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.rent_price"),
          value: "rent_price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.request_date"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AdRequests.actions"),
          value: "actions",
          align: "center",
          width: "200",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      // Start:: Reject Modal Data
      rejectModalIsOpen: false,
      selectedRequestId: null,
      rejectReason: "",
      // End:: Reject Modal Data
    };
  },

  methods: {
    // Start:: Vuex Actions
    ...mapActions({
      updateAppLoadingStatus: "LoadingModule/updateAppLoadingStatus",
    }),
    // End:: Vuex Actions

    // Start:: Get All Data
    async getAllData() {
      this.loading = true;
      this.updateAppLoadingStatus(true);

      try {
        let res = await this.$axios({
          method: "GET",
          url: "ads/requests",
          params: {
            page: this.paginations.current_page,
            advertiser_name: this.filterOptions.advertiser_name,
            name: this.filterOptions.name,
            shop_type: this.filterOptions.shop_type?.id,
            status: this.filterOptions.status?.id,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.updateAppLoadingStatus(false);
        this.tableRows = res.data.data.data;
        this.paginations.current_page = res.data.data.meta.current_page;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        this.updateAppLoadingStatus(false);
        console.log(error.response.data.message);
      }
    },
    // End:: Get All Data

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.advertiser_name = null;
      this.filterOptions.name = null;
      this.filterOptions.shop_type = null;
      this.filterOptions.status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Reset Filter

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      this.paginations.current_page = pagenationValue;
      this.getAllData();
    },
    // End:: Update Router Query Param

    // Start:: Show Item
    showItem(id) {
      this.$router.push({ path: `/ad-requests/show/${id}` });
    },
    // End:: Show Item

    // Start:: Accept Request
    async acceptRequest(id) {
      this.isWaitingRequest = true;
      this.updateAppLoadingStatus(true);

      try {
        await this.$axios({
          method: "POST",
          url: `ads/requests/${id}/accept`,
        });

        this.$message.success(this.$t("MESSAGES.acceptedSuccessfully"));

        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.getAllData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.failed")
        );
      }
    },
    // End:: Accept Request

    // Start:: Open Reject Modal
    openRejectModal(id) {
      this.selectedRequestId = id;
      this.rejectReason = "";
      this.rejectModalIsOpen = true;
    },
    // End:: Open Reject Modal

    // Start:: Close Reject Modal
    closeRejectModal() {
      this.rejectModalIsOpen = false;
      this.selectedRequestId = null;
      this.rejectReason = "";
    },
    // End:: Close Reject Modal

    // Start:: Confirm Reject
    async confirmReject() {
      if (!this.rejectReason) {
        this.$message.error(this.$t("MESSAGES.reject_reason_required"));
        return;
      }

      this.isWaitingRequest = true;
      this.updateAppLoadingStatus(true);

      try {
        await this.$axios({
          method: "POST",
          url: `ads/requests/${this.selectedRequestId}/reject`,
          data: {
            reason: this.rejectReason,
          },
        });

        this.$message.success(this.$t("MESSAGES.rejectedSuccessfully"));

        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.closeRejectModal();
        this.getAllData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.failed")
        );
      }
    },
    // End:: Confirm Reject
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = Number(this.$route.query.page);
    }
    this.getAllData();
    // End:: Fire Methods
  },
};
</script>
